import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { esquemaCorreo } from '@compranorte/contracts'
import { useRecuperar } from '../api/hooks'
import { MarcoAcceso } from '../componentes/MarcoAcceso'
import { Aviso } from '../componentes/ui'

/**
 * Pide el enlace para crear una contraseña nueva.
 *
 * Es la pantalla que la v1 no tenía: decía "pídale a un administrador que la
 * restablezca" y no existía ningún endpoint para hacerlo, así que quien perdía
 * la contraseña perdía la cuenta.
 */
export function Recuperar() {
  const recuperar = useRecuperar()
  const [email, setEmail] = useState('')
  const [errorCampo, setErrorCampo] = useState('')

  function enviar(ev: FormEvent) {
    ev.preventDefault()
    if (recuperar.isPending) return

    const revision = esquemaCorreo.safeParse(email)
    if (!revision.success) {
      setErrorCampo(revision.error.issues[0]?.message ?? 'El correo no es válido')
      return
    }

    setErrorCampo('')
    recuperar.mutate({ email: revision.data })
  }

  const pie = (
    <Link to="/entrar" className="text-[12px] text-acero-500 hover:text-acero-800">
      ← Volver a entrar
    </Link>
  )

  /*
   * El mensaje de éxito es el del servidor, y es deliberadamente ambiguo: dice
   * "si existe una cuenta con ese correo". No confirma si la dirección está
   * registrada, porque si lo hiciera este formulario sería un enumerador de
   * correos abierto a internet. Acá no se adorna ese texto.
   */
  if (recuperar.isSuccess) {
    return (
      <MarcoAcceso pie={pie}>
        <h1 className="text-[17px] font-semibold text-acero-900">Revise su correo</h1>
        <div className="mt-3">
          <Aviso tono="ok">{recuperar.data.mensaje}</Aviso>
        </div>
        <p className="ayuda mt-3">
          El enlace vence en 30 minutos. Si no llega, revise la carpeta de correo no deseado antes
          de pedir otro.
        </p>
        <Link to="/entrar" className="btn btn-secundario w-full mt-4">
          Volver a entrar
        </Link>
      </MarcoAcceso>
    )
  }

  return (
    <MarcoAcceso pie={pie}>
      <h1 className="text-[17px] font-semibold text-acero-900">Recuperar la contraseña</h1>
      <p className="text-[12.5px] text-acero-600 mt-1 leading-relaxed">
        Escriba el correo de su cuenta y le enviamos un enlace para crear una contraseña nueva.
      </p>

      <form onSubmit={enviar} noValidate className="mt-4 space-y-3">
        {recuperar.error && (
          <Aviso tono="error">
            <span role="alert">{recuperar.error.message}</span>
          </Aviso>
        )}

        <label className="block">
          <span className="etiqueta">Correo</span>
          <input
            className={`campo ${errorCampo ? 'campo-error' : ''}`}
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={recuperar.isPending}
          />
          {errorCampo && <p className="error-msg">{errorCampo}</p>}
        </label>

        <button type="submit" className="btn btn-accion w-full" disabled={recuperar.isPending}>
          {recuperar.isPending ? 'Enviando…' : 'Enviarme el enlace'}
        </button>
      </form>
    </MarcoAcceso>
  )
}
